import React, { useState } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { ConfirmationModal } from './layout';

/**
 * @typedef {Omit<React.ComponentProps<typeof ConfirmationModal>, 'open' | 'isLoading'> & {
 * children: React.ReactElement
 * }} ConfirmationModalWrapperProps
 */

/**
 * A wrapper component for a button that opens a confirmation modal when clicked.
 * @param {ConfirmationModalWrapperProps} props
 */
export const ConfirmationModalWrapper = ({ children, onConfirm, onClose, confirmColour = 'secondary', ...props }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpen = () => setOpen(true);

  /**
   * @param {Parameters<NonNullable<ConfirmationModalWrapperProps['onClose']>>} onCloseParams
   */
  const handleOnClose = (...onCloseParams) => {
    setOpen(false);
    onClose?.(...onCloseParams);
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    try {
      await onConfirm?.();
      setOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Clone the child element and add onClick handler */}
      {React.cloneElement(children, { onClick: handleOpen })}

      <ConfirmationModal
        {...props}
        titleIcon={'titleIcon' in props ? props.titleIcon : WarningAmberIcon}
        open={open}
        onClose={handleOnClose}
        onConfirm={handleConfirm}
        confirmColour={confirmColour}
        isLoading={isLoading}
      />
    </>
  );
};
